import {
	IconBus,
	IconDocument,
	IconDriver,
	IconPin,
	IconTime,
	IconTrip,
} from '@flixbus/honeycomb-icons-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from 'pages/WorkAssignments/components/CreateBreakdownFlow/texts';
import { BREAKDOWN_REASON_LABELS } from 'utils/breakdown';
import { formatSummaryDateTime } from 'utils/datetime/fnsTimeFormatter';

const LABELS = CREATE_BREAKDOWN_FLOW_TEXT.summary.rowLabels;

/**
 * Format a station-shaped object as `CODE (Name)`.
 *
 * @param {{ code?: string, name?: string } | null | undefined} station
 * @returns {string}
 */
const formatStation = (station) => {
	if (!station?.code) return '';
	if (!station.name) return station.code;
	return `${station.code} (${station.name})`;
};

const formatSegment = (segment) => {
	if (!segment?.fromStation || !segment?.toStation) return '';
	return `${formatStation(segment.fromStation)} → ${formatStation(segment.toStation)}`;
};

/**
 * Build the "Common" section rows from an accepted `Breakdown` (GraphQL).
 * Read-only counterpart of the flow-side `deriveCommonRows(draft)` — same
 * output shape, different input domain.
 *
 * @param {object} breakdown
 * @param {{ segment?: { fromStation: object, toStation: object } | null }} [options]
 *        `segment` is resolved by the caller (Breakdown carries only the
 *        breakdown location, not the incident segment) — see
 *        `common/BreakdownDetails/deriveBreakdownSegment`.
 * @returns {import('common/BreakdownDetails').BreakdownDetailsRow[]}
 */
export const deriveCommonRowsFromBreakdown = (breakdown, { segment } = {}) => {
	if (!breakdown) return [];

	const rows = [
		{
			label: LABELS.breakdownReason,
			value: BREAKDOWN_REASON_LABELS[breakdown.reason] ?? breakdown.reason ?? '',
			icon: IconBus,
		},
		{
			label: LABELS.incidentTime,
			value: formatSummaryDateTime(breakdown.incidentTimestamp),
			icon: IconTime,
		},
	];

	if (segment?.fromStation && segment?.toStation) {
		rows.push({
			label: LABELS.segment,
			value: formatSegment(segment),
			icon: IconTrip,
		});
	}

	rows.push({
		label: LABELS.breakdownLocation,
		value: formatStation(breakdown.station),
		icon: IconPin,
	});

	if (breakdown.absence?.fromTimestamp) {
		rows.push({
			label: LABELS.originalDriverAbsenceStart,
			value: formatSummaryDateTime(breakdown.absence.fromTimestamp),
			icon: IconDriver,
		});
	}

	if (breakdown.note) {
		rows.push({
			label: LABELS.note,
			value: breakdown.note,
			icon: IconDocument,
		});
	}

	return rows;
};
